// =============================================================================
// PDF PLANNING TERRAIN - Vue simplifiée pour équipes sur le terrain
// Format: Portrait A4, liste par jour, grosses polices lisibles
// =============================================================================

import React from 'react';
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
} from '@react-pdf/renderer';
import { format, startOfWeek, endOfWeek, eachDayOfInterval, isWeekend, getWeek } from 'date-fns';
import { fr } from 'date-fns/locale';
import { COLORS, sharedStyles } from './pdfStyles';
import type { CalendarAffectation, JourDetail } from '@/types/calendar';

// =============================================================================
// STYLES SPÉCIFIQUES AU PLANNING TERRAIN
// =============================================================================

const styles = StyleSheet.create({
  pageOverrides: {
    paddingBottom: 50,
    paddingHorizontal: 30,
  },
  content: {
    paddingTop: 15,
  },
  // Titre principal
  mainTitle: {
    fontSize: 20,
    fontFamily: 'Helvetica-Bold',
    color: COLORS.bleuRoi,
    textAlign: 'center',
    marginBottom: 4,
  },
  filterTitle: {
    fontSize: 14,
    color: COLORS.grisTexte,
    textAlign: 'center',
    marginBottom: 4,
  },
  periodTitle: {
    fontSize: 11,
    color: COLORS.grisTexte,
    textAlign: 'center',
    marginBottom: 15,
  },
  // Carte jour
  dayCard: {
    marginBottom: 12,
    borderWidth: 1,
    borderColor: COLORS.grisClair,
    borderRadius: 6,
    overflow: 'hidden',
  },
  dayHeader: {
    backgroundColor: COLORS.bleuRoi,
    paddingVertical: 8,
    paddingHorizontal: 12,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  dayHeaderWeekend: {
    backgroundColor: '#92400E', // amber-800
  },
  dayHeaderToday: {
    backgroundColor: '#059669', // emerald-600
  },
  dayTitle: {
    fontSize: 14,
    fontFamily: 'Helvetica-Bold',
    color: COLORS.blanc,
    textTransform: 'uppercase',
  },
  dayContent: {
    padding: 12,
    backgroundColor: COLORS.blanc,
  },
  // Chantier dans un jour
  chantierBlock: {
    marginBottom: 10,
    paddingBottom: 10,
    borderBottomWidth: 1,
    borderBottomColor: COLORS.grisClair,
  },
  chantierBlockLast: {
    borderBottomWidth: 0,
    marginBottom: 0,
    paddingBottom: 0,
  },
  chantierHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 6,
  },
  chantierRow: {
    flexDirection: 'row',
    marginBottom: 4,
    alignItems: 'flex-start',
  },
  chantierLabel: {
    fontSize: 9,
    color: COLORS.grisTexte,
    width: 55,
    marginRight: 8,
  },
  chantierName: {
    fontSize: 13,
    fontFamily: 'Helvetica-Bold',
    color: COLORS.grisFonce,
    flex: 1,
  },
  chantierPeriod: {
    fontSize: 10,
    fontFamily: 'Helvetica-Bold',
    color: COLORS.bleuRoi,
    backgroundColor: COLORS.bleuClair,
    paddingVertical: 2,
    paddingHorizontal: 8,
    borderRadius: 4,
  },
  chantierDetail: {
    fontSize: 10,
    color: COLORS.grisTexte,
    flex: 1,
  },
  chantierDetailBold: {
    fontSize: 10,
    fontFamily: 'Helvetica-Bold',
    color: COLORS.grisFonce,
    flex: 1,
  },
  // Membres
  membreBadge: {
    backgroundColor: COLORS.bleuClair,
    borderRadius: 4,
    paddingVertical: 2,
    paddingHorizontal: 6,
    marginRight: 6,
    marginBottom: 4,
  },
  membreText: {
    fontSize: 9,
    color: COLORS.bleuRoi,
    fontFamily: 'Helvetica-Bold',
  },
  // Notes
  notesBlock: {
    marginTop: 6,
    backgroundColor: '#FEF3C7', // amber-100
    borderRadius: 4,
    padding: 8,
  },
  notesLabel: {
    fontSize: 9,
    color: '#92400E', // amber-800
    fontFamily: 'Helvetica-Bold',
    marginBottom: 2,
  },
  notesText: {
    fontSize: 9,
    color: '#92400E', // amber-800
    fontStyle: 'italic',
  },
  // Jour sans affectation
  noAffectation: {
    fontSize: 11,
    color: '#9CA3AF',
    fontStyle: 'italic',
    textAlign: 'center',
    paddingVertical: 8,
  },
});

// =============================================================================
// INTERFACES
// =============================================================================

interface ActiveFilters {
  chantierNames?: string[];
  equipeNames?: string[];
  searchQuery?: string;
}

interface PlanningTerrainPDFProps {
  currentDate: Date;
  affectations: CalendarAffectation[];
  entrepriseNom?: string;
  activeFilters?: ActiveFilters;
}

// =============================================================================
// HELPER : Vérifier si une affectation doit s'afficher pour un jour donné
// =============================================================================

function shouldShowOnDay(aff: CalendarAffectation, day: Date): boolean {
  const dateStr = format(day, 'yyyy-MM-dd');
  const debutStr = aff.date_debut.split('T')[0];
  const finStr = aff.date_fin.split('T')[0];

  // Vérifier si le jour est dans la plage de dates
  if (dateStr < debutStr || dateStr > finStr) {
    return false;
  }

  // Si jours_details est disponible, utiliser cette info précise
  if (aff.jours_details && Array.isArray(aff.jours_details) && aff.jours_details.length > 0) {
    return aff.jours_details.some((jd) => {
      const jdDate = typeof jd.date === 'string' ? jd.date.split('T')[0] : '';
      return jdDate === dateStr;
    });
  }

  // Fallback: logique classique pour les weekends
  if (isWeekend(day)) {
    const debut = new Date(debutStr);
    const fin = new Date(finStr);
    let joursOuvres = 0;
    const current = new Date(debut);
    while (current <= fin) {
      if (!isWeekend(current)) joursOuvres++;
      current.setDate(current.getDate() + 1);
    }
    const joursTravailles = aff.jours_travailles || 0;
    return joursTravailles > joursOuvres;
  }

  return true;
}

// =============================================================================
// HELPER : Obtenir la période de travail pour un jour donné depuis jours_details
// Retourne la période exacte stockée dans les données
// =============================================================================

function getPeriodForDay(aff: CalendarAffectation, day: Date): 'AM' | 'PM' | 'FULL' {
  const dateStr = format(day, 'yyyy-MM-dd');

  if (aff.jours_details && Array.isArray(aff.jours_details) && aff.jours_details.length > 0) {
    // Filtrer TOUTES les entrées pour ce jour (il peut y avoir AM + PM séparément)
    const joursForDate = aff.jours_details.filter((jd: JourDetail) => {
      const jdDate = typeof jd.date === 'string' ? jd.date.split('T')[0] : '';
      return jdDate === dateStr;
    });

    if (joursForDate.length > 0) {
      // Vérifier si AM et PM sont tous les deux présents = jour complet
      const hasAM = joursForDate.some(jd => jd.period === 'AM');
      const hasPM = joursForDate.some(jd => jd.period === 'PM');
      const hasFULL = joursForDate.some(jd => jd.period === 'FULL');

      if (hasFULL || (hasAM && hasPM)) {
        return 'FULL'; // Journée entière
      }
      if (hasAM) return 'AM';
      if (hasPM) return 'PM';
    }
  }

  // Sans jours_details, c'est une journée complète par défaut
  return 'FULL';
}

// =============================================================================
// HELPER : Formater la période en français
// =============================================================================

function formatPeriod(period: 'AM' | 'PM' | 'FULL'): string {
  switch (period) {
    case 'AM':
      return 'Matinee';
    case 'PM':
      return 'Apres-midi';
    case 'FULL':
    default:
      return 'Journee entiere';
  }
}

// =============================================================================
// HELPER : Formater le nom d'un membre (Prénom + initiale nom)
// =============================================================================

function formatMembreName(membre: { prenom: string; nom: string } | undefined): string {
  if (!membre) return '?';
  const prenom = membre.prenom || '';
  const nom = membre.nom || '';
  return `${prenom} ${nom.charAt(0)}.`.trim();
}

// =============================================================================
// COMPOSANTS HELPER
// =============================================================================

const Header = ({ entrepriseNom, activeFilters, currentDate }: {
  entrepriseNom?: string;
  activeFilters?: ActiveFilters;
  currentDate: Date;
}) => {
  const mainTitle = 'PLANNING TERRAIN';
  let filterTitle = '';

  if (activeFilters?.equipeNames && activeFilters.equipeNames.length === 1) {
    filterTitle = `Equipe ${activeFilters.equipeNames[0]}`;
  } else if (activeFilters?.chantierNames && activeFilters.chantierNames.length === 1) {
    filterTitle = activeFilters.chantierNames[0];
  } else if (activeFilters?.equipeNames && activeFilters.equipeNames.length > 1) {
    filterTitle = `${activeFilters.equipeNames.length} equipes`;
  } else if (activeFilters?.chantierNames && activeFilters.chantierNames.length > 1) {
    filterTitle = `${activeFilters.chantierNames.length} chantiers`;
  }

  const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
  const weekEnd = endOfWeek(currentDate, { weekStartsOn: 1 });
  const periodText = `Semaine ${getWeek(currentDate, { weekStartsOn: 1 })} - du ${format(weekStart, 'd', { locale: fr })} au ${format(weekEnd, 'd MMMM yyyy', { locale: fr })}`;

  return (
    <>
      <View style={sharedStyles.header}>
        <View style={sharedStyles.headerLeft}>
          <Text style={sharedStyles.logoText}>B<Text style={{ color: '#F59E0B' }}>8</Text>ild</Text>
        </View>
        <View style={sharedStyles.headerRight}>
          <Text style={sharedStyles.headerTitle}>{mainTitle}</Text>
          <Text style={sharedStyles.headerSubtitle}>
            {entrepriseNom || 'Entreprise'} - {format(new Date(), 'dd/MM/yyyy')}
          </Text>
        </View>
      </View>
      <View style={sharedStyles.bandeDore} />
      <View style={styles.content}>
        {filterTitle && (
          <Text style={styles.filterTitle}>{filterTitle}</Text>
        )}
        <Text style={styles.periodTitle}>{periodText}</Text>
      </View>
    </>
  );
};

const Footer = () => (
  <View
    style={{
      position: 'absolute',
      bottom: 20,
      left: 30,
      right: 30,
      flexDirection: 'row',
      justifyContent: 'space-between',
      borderTopWidth: 1,
      borderTopColor: COLORS.grisClair,
      paddingTop: 8,
    }}
    fixed
  >
    <Text style={{ fontSize: 7, color: COLORS.grisTexte }}>
      Genere par B8ild le {format(new Date(), 'dd/MM/yyyy a HH:mm', { locale: fr })}
    </Text>
    <Text
      style={{ fontSize: 7, color: COLORS.grisTexte }}
      render={({ pageNumber, totalPages }) => `Page ${pageNumber} / ${totalPages}`}
    />
  </View>
);

// =============================================================================
// COMPOSANT JOUR
// =============================================================================

interface DayViewProps {
  day: Date;
  affectations: CalendarAffectation[];
  isToday: boolean;
}

const DayView = ({ day, affectations, isToday }: DayViewProps) => {
  const isWeekendDay = isWeekend(day);
  const dayAffectations = affectations.filter((aff) => shouldShowOnDay(aff, day));

  // Grouper par chantier
  const byChantier: Record<string, CalendarAffectation[]> = {};
  dayAffectations.forEach((aff) => {
    const chantierId = aff.chantier_id;
    if (!byChantier[chantierId]) {
      byChantier[chantierId] = [];
    }
    byChantier[chantierId].push(aff);
  });

  const chantierGroups = Object.values(byChantier);
  const hasAffectations = chantierGroups.length > 0;

  // Jour sans affectation : affichage compact sur une ligne
  if (!hasAffectations) {
    return (
      <View style={{ flexDirection: 'row', marginBottom: 4 }} wrap={false}>
        <View
          style={{
            backgroundColor: isWeekendDay ? '#92400E' : COLORS.bleuRoi,
            paddingVertical: 4,
            paddingHorizontal: 10,
            borderRadius: 4,
            marginRight: 8,
          }}
        >
          <Text style={{ fontSize: 10, fontFamily: 'Helvetica-Bold', color: COLORS.blanc }}>
            {format(day, 'EEE d', { locale: fr }).toUpperCase()}
          </Text>
        </View>
        <Text style={{ fontSize: 10, color: '#9CA3AF', fontStyle: 'italic', alignSelf: 'center' }}>
          Pas de chantier
        </Text>
      </View>
    );
  }

  return (
    <View style={styles.dayCard} wrap={false}>
      <View
        style={[
          styles.dayHeader,
          isWeekendDay && styles.dayHeaderWeekend,
          isToday && styles.dayHeaderToday,
        ]}
      >
        <Text style={styles.dayTitle}>
          {format(day, 'EEEE d MMMM', { locale: fr })}
        </Text>
      </View>
      <View style={styles.dayContent}>
        {
          chantierGroups.map((chantierAffs, idx) => {
            const chantier = chantierAffs[0]?.chantier;
            const firstAff = chantierAffs[0];
            const isLast = idx === chantierGroups.length - 1;

            // Obtenir la période pour CE chantier CE jour
            // On prend la période de la première affectation du groupe
            const chantierPeriod = getPeriodForDay(firstAff, day);

            // Collecter tous les membres uniques
            const membresUniques = chantierAffs
              .map((aff) => aff.membre)
              .filter((m, i, arr) => m && arr.findIndex((x) => x?.id === m?.id) === i);

            // Collecter les notes uniques
            const notes = chantierAffs
              .map((aff) => aff.notes)
              .filter((n, i, arr) => n && arr.indexOf(n) === i);

            return (
              <View
                key={idx}
                style={[styles.chantierBlock, isLast && styles.chantierBlockLast]}
              >
                {/* Header chantier avec nom et période */}
                <View style={styles.chantierHeader}>
                  <Text style={styles.chantierName}>
                    {chantier?.nom_chantier || 'Chantier'}
                  </Text>
                  <Text style={styles.chantierPeriod}>
                    {formatPeriod(chantierPeriod)}
                  </Text>
                </View>

                {/* Adresse */}
                {chantier?.adresse && (
                  <View style={styles.chantierRow}>
                    <Text style={styles.chantierLabel}>Adresse</Text>
                    <Text style={styles.chantierDetail}>{chantier.adresse}</Text>
                  </View>
                )}

                {/* Téléphone client */}
                {chantier?.telephone_client && (
                  <View style={styles.chantierRow}>
                    <Text style={styles.chantierLabel}>Tel. client</Text>
                    <Text style={styles.chantierDetail}>
                      {chantier.telephone_client}
                      {chantier.client ? ` (${chantier.client})` : ''}
                    </Text>
                  </View>
                )}

                {/* Membres */}
                {membresUniques.length > 0 && (
                  <View style={styles.chantierRow}>
                    <Text style={styles.chantierLabel}>Equipe</Text>
                    <View style={{ flex: 1, flexDirection: 'row', flexWrap: 'wrap' }}>
                      {membresUniques.map((membre, mIdx) => (
                        <View key={mIdx} style={styles.membreBadge}>
                          <Text style={styles.membreText}>
                            {formatMembreName(membre)}
                          </Text>
                        </View>
                      ))}
                    </View>
                  </View>
                )}

                {/* Notes/Consignes */}
                {notes.length > 0 && (
                  <View style={styles.notesBlock}>
                    <Text style={styles.notesLabel}>Consignes :</Text>
                    <Text style={styles.notesText}>
                      {notes.join(' | ')}
                    </Text>
                  </View>
                )}
              </View>
            );
          })
        }
      </View>
    </View>
  );
};

// =============================================================================
// DOCUMENT PRINCIPAL
// =============================================================================

export const PlanningTerrainPDF = ({
  currentDate,
  affectations,
  entrepriseNom,
  activeFilters,
}: PlanningTerrainPDFProps) => {
  const start = startOfWeek(currentDate, { weekStartsOn: 1 });
  const end = endOfWeek(currentDate, { weekStartsOn: 1 });
  const weekDays = eachDayOfInterval({ start, end });
  const today = new Date();
  const todayStr = format(today, 'yyyy-MM-dd');

  return (
    <Document>
      <Page size="A4" orientation="portrait" style={[sharedStyles.page, styles.pageOverrides]}>
        <Header
          entrepriseNom={entrepriseNom}
          activeFilters={activeFilters}
          currentDate={currentDate}
        />

        <View style={styles.content}>
          {weekDays.map((day) => {
            const dateKey = format(day, 'yyyy-MM-dd');
            const isToday = dateKey === todayStr;

            return (
              <DayView
                key={dateKey}
                day={day}
                affectations={affectations}
                isToday={isToday}
              />
            );
          })}
        </View>

        <Footer />
      </Page>
    </Document>
  );
};

export default PlanningTerrainPDF;
